import React from 'react';
import { useRequest } from 'ahooks';
import { createForm, onFieldValueChange } from '@formily/core';
import findLastKey from 'lodash/findLastKey';
import isEmpty from 'lodash/isEmpty';
import cloneDeep from 'lodash/cloneDeep';
import { CnForm } from '@/form/cn-form';
import { CnLoading } from '@/components/cn-loading';
import { generateSchema, handleRequestService, useStateCallback, } from '../utils';
const DynamicContainerKey = 'dynamic_container';
const DynamicFormEffectsKey = 'dynamicFormAddEffectsKey';
export const CnDynamicForm = React.forwardRef((props, ref) => {
    const prevFieldRef = React.useRef(null);
    const { schema, cols, formProps, remoteUrl: _remoteUrl, requestConfig, initRequestConfig, submitRequestConfig, form: initForm, onSubmit: initOnSubmit, ...restProps } = props;
    const realSchema = React.useMemo(() => generateSchema(schema, cols), [schema, cols]);
    const [realRequestConfig, setRequestConfig] = useStateCallback({
        ...requestConfig,
        fromProps: true,
        url: requestConfig?.url || _remoteUrl,
    });
    const [realSubmitRequestConfig, setSubmitRequestConfig] = useStateCallback(submitRequestConfig);
    // 只有props内的requestConfig才存在initRequest；通过reLoad一定会执行run函数，因此何时调用reLoad，何时就会触发run；
    // 如果配置了url，但是没配置refreshDeps，则认为是初始渲染直接获取动态Schema
    const isInitRequest = React.useMemo(() => realRequestConfig?.url &&
        realRequestConfig?.fromProps &&
        isEmpty(realRequestConfig?.refreshDeps), [realRequestConfig]);
    // 远程Schema
    const { service, searchFormat, refreshDeps, formatResult = (res) => {
        if (Array.isArray(res)) {
            return res;
        }
        else if (Array.isArray(res?.data)) {
            return res.data;
        }
        else if (Array.isArray(res?.data?.schema)) {
            return res.data.schema;
        }
        return [];
    }, ...otherConfig } = realRequestConfig || {};
    const requestService = React.useCallback(handleRequestService(realRequestConfig), [realRequestConfig]);
    const { run, data = [], loading, } = useRequest(requestService, {
        ready: !!(realRequestConfig.url || service),
        manual: true,
        ...otherConfig,
    });
    // 当props中requestConfig含有url && 且refreshDeps为空，则认为是isInitRequest，则调用run获取远程Schema。
    React.useEffect(() => {
        if (isInitRequest) {
            run();
        }
    }, [isInitRequest]);
    // 两种情况，一种是外部注入form，一种是内部createForm；
    // 需要判断，外部注入的话，需要获取form后，执行addEffects；内部生成的话需要获取form后，执行addEffects；
    const finalForm = React.useMemo(() => {
        // 如果涉及到状态的更新，须传入form实例
        if (initForm) {
            initForm.addEffects(DynamicFormEffectsKey, (form) => {
                if (refreshDeps && refreshDeps.length > 0) {
                    onFieldValueChange(`*(${refreshDeps.join(',')})`, (field) => {
                        run({
                            watchValue: field.value,
                            formValues: form.getFormState().values,
                        });
                    });
                }
            });
            return initForm;
        }
        return createForm({
            ...formProps,
            effects: (form) => {
                if (refreshDeps && refreshDeps.length > 0) {
                    onFieldValueChange(`*(${refreshDeps.join(',')})`, (field) => {
                        run({
                            watchValue: field.value,
                            formValues: form.getFormState().values,
                        });
                    });
                }
                formProps?.effects && formProps.effects(form);
            },
        });
    }, [initForm, realSchema]);
    const finalSchema = React.useMemo(() => {
        // 清除字段模型、删除字段值，确保异步Schema不会值缓存
        if (prevFieldRef.current && prevFieldRef.current.length > 0) {
            finalForm.clearFormGraph(`*.${DynamicContainerKey}.*`);
            finalForm.deleteValuesIn(`*.${DynamicContainerKey}.*`);
        }
        const cloneRealSchema = cloneDeep(realSchema);
        // 如果接口返回为空，则直接返回初始Schema；
        if (isEmpty(data))
            return cloneRealSchema;
        // 如果接口返回不为空，则进行format；并构建最终Schema进行渲染
        const initFormatData = formatResult(data);
        let formatData = [];
        if (Array.isArray(initFormatData)) {
            formatData = initFormatData;
        }
        else if (Array.isArray(initFormatData?.schema)) {
            formatData = initFormatData.schema;
        }
        else if (Array.isArray(initFormatData?.data)) {
            formatData = initFormatData.data;
        }
        else if (Array.isArray(initFormatData?.data?.schema)) {
            formatData = initFormatData.data.schema;
        }
        prevFieldRef.current = formatData;
        const realData = formatData;
        // 如果format后的data为空，则直接返回静态Schema
        if (isEmpty(realData)) {
            return cloneRealSchema;
        }
        else if (isEmpty(cloneRealSchema)) {
            // 不存在静态Schema时，直接使用接口返回进行渲染
            return generateSchema(realData, cols);
        }
        else {
            // 存在静态Schema时，查找到静态Schema最后一个Properties对象，将接口返回Schema塞进去
            const lastProperties = findLastKey(cloneRealSchema?.properties, (o) => {
                return !isEmpty(o?.properties);
            });
            // 将异步获取的Schema，添加至最后一个properties中，确保异步Schema在表单最后位置
            if (lastProperties) {
                const targetProperties = cloneRealSchema?.properties[lastProperties]?.properties;
                targetProperties[DynamicContainerKey] = {
                    type: 'void',
                    'x-component': 'CnFormGrid',
                    'x-component-props': {
                        cols,
                    },
                    colSpan: 'full',
                    properties: generateSchema(realData, cols, true),
                };
            }
        }
        return cloneRealSchema;
    }, [data, realSchema, cols, finalForm]);
    // 初始值数据源
    const { url: initUrl, service: initService, formatResult: initFormatResult = (res) => {
        return res?.data || res || {};
    }, ...initRestConfig } = initRequestConfig || {};
    const initRequestService = React.useCallback(handleRequestService(initRequestConfig), [initRequestConfig]);
    const { run: initRun, data: initData = {} } = useRequest(initRequestService, {
        ready: !!(initUrl || initService),
        manual: true,
        ...initRestConfig,
    });
    React.useEffect(() => {
        initRun();
    }, [initRequestConfig]);
    React.useEffect(() => {
        const initialValues = initFormatResult(initData);
        if (!isEmpty(initialValues)) {
            finalForm.setInitialValues(initialValues);
        }
    }, [initData, finalForm]);
    // 提交数据源
    const { url: submitUrl, service: submitService, 
    // eslint-disable-next-line
    formatResult: submitFormatResult = (res) => {
        return res?.data || res || {};
    }, ...submitRestConfig } = realSubmitRequestConfig || {};
    const submitRequestService = React.useCallback(handleRequestService(realSubmitRequestConfig), [realSubmitRequestConfig]);
    const { run: submitRun } = useRequest(submitRequestService, {
        ready: !!(submitUrl || submitService),
        manual: true,
        ...submitRestConfig,
    });
    // 根据onSubmit、submitRequestConfig重写onSubmit函数
    const onSubmit = React.useCallback((values) => {
        initOnSubmit && initOnSubmit?.(values);
        submitRun({ formValues: values });
    }, [initOnSubmit, realSubmitRequestConfig]);
    React.useImperativeHandle(ref, () => ({
        reLoad: (_requestConfig) => {
            setRequestConfig({ ..._requestConfig, fromProps: false }, () => {
                run();
            });
        },
        submit: (_requestConfig) => {
            setSubmitRequestConfig(_requestConfig, () => {
                const formValues = finalForm.getFormState().values;
                onSubmit(formValues);
            });
        },
    }));
    return (React.createElement(CnLoading, { visible: loading, style: { width: '100%' } },
        React.createElement(CnForm, { ...restProps, form: finalForm, schema: finalSchema, onSubmit: onSubmit })));
});
